// The Compensation queue — `#compensation` (DC-32 "Compensation adds Compensation queue"; G §5 "Compensation: Pending ->
// In Review -> Finalized."). Compensation's reviews, keyed on the Hiring Request (the requisition is set when it is
// created): the ones naming the viewer and the non-confidential ones their scope covers — ids, the job and the status,
// never a figure (`comp_review.queue`). Tabs by status with counts, the active tab's table inside its tabpanel; each row
// opens the worksheet `#compensation/RQ-####`.
// Hosted by MissionShell: views/b2/register.ts registers it through S12's registerView, so it draws no McRoot of its own.
// The nav row is lit from `shell_vocab` (backend app/tools/comp_review.py, gated by `comp_review.queue`).

import { useEffect, useState } from "react";
import { compReviewQueue, type QueueRow } from "@/api/tools";
import { isClientRefusal } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  Banner, COMPENSATION_REVIEW_STATUS, DEFAULT_TIME_ZONE, ErrorStrip, LEVEL_DISPLAY, NAV_ITEM_LABELS, PageHeader, Paragraph, Pill, RULE_LABELS,
  Stack, Table, Tabs, Td, TextLink, Th, Tr, formatDate, toEpoch, useMc, type Level,
} from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import { REQ_LABELS } from "../requisition/labels";
import { COMP_RULES, noCompensationReview } from "./labels";

const TABS = [
  { key: "pending", label: COMPENSATION_REVIEW_STATUS.pending },
  { key: "in_review", label: COMPENSATION_REVIEW_STATUS.inReview },
  { key: "finalized", label: COMPENSATION_REVIEW_STATUS.finalized },
] as const;

export function CompensationQueue() {
  const { register } = useViewState();
  useEffect(() => register({ view: "compensation", ids: {}, summary: "compensation queue" }), [register]);
  return (
    <QueueBody />
  );
}

function QueueBody() {
  const { me } = useSession();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  const q = useSettledRead(["comp-review-queue"], () => compReviewQueue({ page_size: 200 }));
  const [tab, setTab] = useState<string>("pending");
  if (q.error?.status === 404) return <NotFound route="compensation" />;
  const items = q.data?.items ?? [];
  const shown = items.filter((row) => row.status === tab);
  const table = (
    <Table
      label={NAV_ITEM_LABELS.compensationQueue}
      loading={q.isPending ? 4 : undefined}
      empty={q.data && shown.length === 0 ? noCompensationReview({ status: WORD[tab] ?? tab }) : undefined}
      cols={6}
      head={(
        <Tr>
          <Th>{REQ_LABELS.hiringRequest}</Th>
          <Th>{REQ_LABELS.requisition}</Th>
          <Th>{REQ_LABELS.title}</Th>
          <Th>{REQ_LABELS.department}</Th>
          <Th>{REQ_LABELS.compensationReview}</Th>
          <Th>{REQ_LABELS.daysOpen}</Th>
        </Tr>
      )}
    >
      {shown.map((row, index) => <Row key={row.review_id} row={row} index={index} timeZone={timeZone} />)}
    </Table>
  );
  return (
    <Stack gap={16}>
      <PageHeader title={NAV_ITEM_LABELS.compensationQueue} />
      <Paragraph>{COMP_RULES.task}</Paragraph>
      {q.error && isClientRefusal(q.error) ? <Banner tone="red" live>{q.error.remedy || q.error.message}</Banner> : null}
      {q.error && !isClientRefusal(q.error) ? (
        <ErrorStrip
          message={q.error.remedy || RULE_LABELS.failedSyncRetryable}
          problem={{ status: q.error.status, refusal_code: q.error.code, detail: q.error.message, request_id: q.error.requestId }}
          onRetry={() => void q.refetch()}
          
        />
      ) : null}
      {/* the active tab's rows sit inside its tabpanel (UAT F-54) */}
      <Tabs
        label={NAV_ITEM_LABELS.compensationQueue}
        active={tab}
        onSelect={setTab}
        items={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          count: q.data ? items.filter((row) => row.status === t.key).length : undefined,
          panel: t.key === tab ? table : null,
        }))}
      />
    </Stack>
  );
}

const WORD: Readonly<Record<string, string>> = {
  pending: COMPENSATION_REVIEW_STATUS.pending, in_review: COMPENSATION_REVIEW_STATUS.inReview, finalized: COMPENSATION_REVIEW_STATUS.finalized,
};

/** One review. At ≤ 480 the table draws each row as a card whose cells read their column's words beside one value, so a
 * value and its second line (the level, the opening date) sit in one block that the card never splits (UAT F-86). */
function Row({ row, index, timeZone }: { row: QueueRow; index: number; timeZone: string }) {
  const { now } = useMc();
  const days = Math.max(0, Math.floor((now - toEpoch(row.opened_at)) / 86_400_000));
  const level = row.level in LEVEL_DISPLAY ? LEVEL_DISPLAY[row.level as Level] : row.level;
  return (
    <Tr >
      <Td label={REQ_LABELS.hiringRequest} mono nowrap><TextLink href={`#compensation/${row.request_id}`} >{row.request_id}</TextLink></Td>
      <Td label={REQ_LABELS.requisition} mono nowrap>{row.requisition_id ?? "—"}</Td>
      <Td label={REQ_LABELS.title}><span>{row.title}<span className="mc-sub">{level}</span></span></Td>
      <Td label={REQ_LABELS.department}>{row.department ?? "—"}</Td>
      <Td label={REQ_LABELS.compensationReview}><Pill>{WORD[row.status] ?? row.status}</Pill></Td>
      <Td label={REQ_LABELS.daysOpen} nowrap><span>{days}<span className="mc-sub">{formatDate(row.opened_at, timeZone)}</span></span></Td>
    </Tr>
  );
}
